import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { MembershipTier, SessionProfile } from '@/domains/auth/types/auth.types';

// Client copy of the signed-in user. The server session cookie is the truth;
// this is refreshed by /authentication/check-status and cleared on a 401.
interface AuthState {
  user_id: string;
  group_id: string;
  isLogin: boolean;
  lang: string;
  name: string;
  email: string;
  picture_url: string;
  membership_tier: MembershipTier;
  is_admin: boolean;
}

interface AuthActions {
  setAuth: (profile: SessionProfile) => void;
  clearAuth: () => void;
  setLang: (lang: string) => void;
  sendLogoutSignal: () => void;
}

const initialState: AuthState = {
  user_id: '',
  group_id: '',
  isLogin: false,
  lang: 'ind',
  name: '',
  email: '',
  picture_url: '',
  membership_tier: 'member',
  is_admin: false,
};

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set) => ({
      ...initialState,

      setAuth: (profile) =>
        set({
          user_id: profile.user_id,
          group_id: profile.group_id == null ? '' : String(profile.group_id),
          isLogin: true,
          name: profile.name ?? '',
          email: profile.email ?? '',
          picture_url: profile.picture_url ?? '',
          membership_tier: profile.membership_tier,
          is_admin: profile.is_admin,
        }),

      clearAuth: () => {
        // Clean up legacy keys from before zustand migration
        localStorage.removeItem('user_id');
        localStorage.removeItem('group_id');
        localStorage.removeItem('isLogin');
        localStorage.removeItem('lang');
        sessionStorage.removeItem('user_id');
        sessionStorage.removeItem('group_id');
        sessionStorage.removeItem('isLogin');

        set(initialState);
      },

      setLang: (lang: string) => set({ lang }),

      sendLogoutSignal: () => {
        // Write directly to localStorage for cross-tab StorageEvent detection
        const signal = JSON.stringify({ value: false, time: Date.now() });
        localStorage.setItem('logout_signal', signal);
      },
    }),
    {
      name: 'auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user_id: state.user_id,
        group_id: state.group_id,
        isLogin: state.isLogin,
        lang: state.lang,
        name: state.name,
        email: state.email,
        picture_url: state.picture_url,
        membership_tier: state.membership_tier,
        is_admin: state.is_admin,
      }),
    }
  )
);
